'use client';

/**
 * ComposerPlusMenu
 *
 * The "+" affordance that lives in the bottom-left of a Meridian chat input.
 * Opens a single, quiet popover that groups the ways to bring context into a
 * message: attach files, attach screenshots, connect apps (MCP), and reach for
 * a skill. Items are only rendered when the host wires the matching handler, so
 * the same menu stays honest on the start screen (no chat yet → no uploads) and
 * inside an active chat (everything available).
 *
 * Presentational only — file pickers / uploads / skill modals live in the host.
 */

import React, { memo, useCallback } from 'react';
import { Plus, Paperclip, Image as ImageIcon, Plug, Sparkles, type LucideIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@west-tennessee-consulting/ui/components/popover';

export interface ComposerPlusMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  /** Attach documents/images. Omit to hide the row (e.g. on the start screen). */
  onAddFiles?: () => void;
  /** Attach a screenshot (image-only picker). Omit to hide the row. */
  onAddScreenshots?: () => void;
  /** Open the skills picker. Omit to hide the row. */
  onUseSkill?: () => void;
  /** Link to the MCP connections page. Omit to hide the "Connect apps" row. */
  mcpConnectionsHref?: string;
  side?: 'top' | 'bottom';
  align?: 'start' | 'end';
  /** Classes applied to the trigger button so hosts can match their input chrome. */
  triggerClassName?: string;
}

interface MenuRowProps {
  icon: LucideIcon;
  label: string;
  hint?: string;
  onClick: () => void;
}

function MenuRow({ icon: Icon, label, hint, onClick }: MenuRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
    >
      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-foreground">{label}</span>
        {hint && <span className="block truncate text-xs text-muted-foreground">{hint}</span>}
      </span>
    </button>
  );
}

const DEFAULT_TRIGGER_CLASS =
  'inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50';

function ComposerPlusMenuComponent({
  open,
  onOpenChange,
  disabled = false,
  onAddFiles,
  onAddScreenshots,
  onUseSkill,
  mcpConnectionsHref,
  side = 'top',
  align = 'start',
  triggerClassName,
}: ComposerPlusMenuProps) {
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const handleAddFiles = useCallback(() => {
    close();
    onAddFiles?.();
  }, [close, onAddFiles]);

  const handleAddScreenshots = useCallback(() => {
    close();
    onAddScreenshots?.();
  }, [close, onAddScreenshots]);

  const handleUseSkill = useCallback(() => {
    close();
    onUseSkill?.();
  }, [close, onUseSkill]);

  const hasAnyItem = Boolean(onAddFiles || onAddScreenshots || mcpConnectionsHref || onUseSkill);
  if (!hasAnyItem) return null;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={triggerClassName ?? DEFAULT_TRIGGER_CLASS}
          aria-label="Add context"
          title="Add files, connect apps, or use a skill"
        >
          <Plus className="h-5 w-5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} side={side} sideOffset={8} className="w-64 p-1.5">
        {onAddFiles && (
          <MenuRow icon={Paperclip} label="Add files" hint="Documents & images" onClick={handleAddFiles} />
        )}
        {onAddScreenshots && (
          <MenuRow icon={ImageIcon} label="Add screenshots" hint="Upload an image" onClick={handleAddScreenshots} />
        )}
        {mcpConnectionsHref && (
          <a
            href={mcpConnectionsHref}
            onClick={close}
            className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
          >
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <Plug className="h-4 w-4" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-foreground">Connect apps</span>
              <span className="block truncate text-xs text-muted-foreground">MCP connections</span>
            </span>
          </a>
        )}
        {onUseSkill && (
          <MenuRow icon={Sparkles} label="Use a skill" hint="Attach a playbook" onClick={handleUseSkill} />
        )}
      </PopoverContent>
    </Popover>
  );
}

export const ComposerPlusMenu = memo(ComposerPlusMenuComponent);
